import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, List, ChevronLeft, ChevronRight } from 'lucide-react';
import { InlineLoadingState, LoadingSpinner } from '../../../components/LoadingSpinner';
import { ausschreibungDetailPath, ausschreibungenListPath } from '../../../utils/ausschreibungRoutes';
import { formatTenderGrossEuro } from '../offerDraftGross';
import type { AusschreibungListItem, AusschreibungStatus } from '../types';

interface Props {
  selectedId?: string;
  optimisticItem?: AusschreibungListItem;
  refreshKey?: number | string;
  /** Auf der Listen-Seite ausblenden — dort ist man bereits in der Tabellenansicht. */
  hideFooterLink?: boolean;
  /** In der Positions-Ansicht: Leiste eingeklappt starten (ausklappbar); sonst Präferenz aus localStorage. */
  reviewPhase?: boolean;
}

const daysUntil = (iso: string | null): number | null => {
  if (!iso) return null;
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
};

const fmtDate = (iso: string | null): string => {
  if (!iso) return '';
  const date = iso.length >= 10 ? iso.slice(0, 10) : iso;
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [y, m, d] = date.split('-');
    return `${d}.${m}.${y}`;
  }
  return iso;
};

const shortDisplayId = (display_id: string | null, id: string): string => {
  if (!display_id) return id.slice(0, 8);
  const parts = display_id.split('-');
  const last = parts[parts.length - 1];
  return last || display_id;
};

type SectionDot = 'blue' | 'gray' | 'orange' | 'violet' | 'muted';

const dotClass: Record<SectionDot, string> = {
  blue: 'bg-blue-500',
  gray: 'bg-slate-400',
  orange: 'bg-orange-500',
  violet: 'bg-violet-500',
  muted: 'bg-slate-300',
};

const SIDEBAR_COLLAPSED_KEY = 'meister.ausschreibungen.sidebarCollapsed';

export const AusschreibungListSidebar: React.FC<Props> = ({
  selectedId,
  optimisticItem,
  refreshKey,
  hideFooterLink,
  reviewPhase = false,
}) => {
  const navigate = useNavigate();
  const [items, setItems] = React.useState<AusschreibungListItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [moreOpen, setMoreOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  const setCollapsedPersist = React.useCallback((next: boolean) => {
    setCollapsed(next);
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, []);

  React.useEffect(() => {
    if (reviewPhase) {
      setCollapsed(true);
      return;
    }
    try {
      setCollapsed(localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1');
    } catch {
      setCollapsed(false);
    }
  }, [reviewPhase]);

  const fetchItems = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/ausschreibungen', { credentials: 'include' });
      const raw = (await res.json()) as AusschreibungListItem[] | { error?: string };
      if (!res.ok) {
        const msg =
          raw && typeof raw === 'object' && !Array.isArray(raw) && typeof raw.error === 'string'
            ? raw.error
            : 'Laden fehlgeschlagen.';
        throw new Error(msg);
      }
      const data = raw as AusschreibungListItem[];
      setItems(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void fetchItems();
  }, [fetchItems, refreshKey]);

  const merged = React.useMemo(() => {
    if (!optimisticItem) return items;
    const idx = items.findIndex((it) => it.id === optimisticItem.id);
    if (idx === -1) return [optimisticItem, ...items];
    const next = items.slice();
    next[idx] = { ...next[idx], ...optimisticItem };
    return next;
  }, [items, optimisticItem]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return merged;
    return merged.filter((it) =>
      [
        it.display_id,
        it.title,
        it.customer_name,
        it.customer_company,
        it.customer_city,
        it.customer_street,
      ]
        .map((v) => String(v ?? '').toLowerCase())
        .join(' ')
        .includes(q),
    );
  }, [merged, search]);

  const inProgress = filtered.filter((it) => it.status === 'inprogress');
  const drafts = filtered.filter((it) => it.status === 'draft');
  const ready = filtered.filter((it) => it.status === 'ready');
  const sent = filtered.filter((it) => it.status === 'sent');
  const finished = filtered.filter((it) => it.status === 'won' || it.status === 'lost');
  const moreCount = sent.length + finished.length;

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const res = await fetch('/api/ausschreibungen', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Anlegen fehlgeschlagen.');
      const created = (await res.json()) as AusschreibungListItem;
      setItems((prev) => [created, ...prev]);
      navigate(ausschreibungDetailPath({ id: created.id, display_id: created.display_id }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  };

  return (
    <aside
      className={`hidden min-h-0 shrink-0 flex-col border-r border-app-border bg-app-bg transition-[width] duration-200 ease-out md:flex ${
        collapsed ? 'w-[52px]' : 'w-[280px]'
      }`}
    >
      {collapsed ? (
        <div className="flex flex-col items-center gap-3 py-3 px-1 border-b border-app-border/80 bg-app-surface shrink-0">
          <button
            type="button"
            onClick={() => setCollapsedPersist(false)}
            className="p-0.5 text-app-text-subtle hover:text-app-text transition-colors"
            title="Seitenleiste ausklappen"
            aria-label="Seitenleiste ausklappen"
          >
            <ChevronRight className="w-4 h-4" strokeWidth={1.5} aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => void handleCreate()}
            disabled={creating}
            className="p-1.5 rounded-md text-pipedrive-blue hover:bg-app-hover disabled:opacity-50 transition-colors"
            title="Neue Ausschreibung"
            aria-label="Neue Ausschreibung"
          >
            {creating ? (
              <LoadingSpinner />
            ) : (
              <Plus className="w-4 h-4" strokeWidth={1.5} />
            )}
          </button>
        </div>
      ) : (
        <div className="px-3 py-3 border-b border-app-border/80 bg-app-surface space-y-2 shrink-0">
          <div className="flex items-center justify-between gap-1">
            <p className="text-sm font-medium text-app-text-muted truncate min-w-0">
              Ausschreibungen ({merged.length})
            </p>
            <div className="flex items-center shrink-0 gap-0.5">
              <button
                type="button"
                onClick={() => void handleCreate()}
                disabled={creating}
                className="inline-flex h-7 items-center gap-1 rounded-md bg-pipedrive-green px-2.5 text-xs font-semibold text-white transition-colors hover:bg-[#218838] disabled:opacity-60"
              >
                {creating ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <Plus className="w-3.5 h-3.5" />
                )}
                Neu
              </button>
              <button
                type="button"
                onClick={() => setCollapsedPersist(true)}
                className="p-0.5 text-app-text-subtle hover:text-app-text transition-colors"
                title="Seitenleiste einklappen"
                aria-label="Seitenleiste einklappen"
              >
                <ChevronLeft className="w-4 h-4" strokeWidth={1.5} aria-hidden />
              </button>
            </div>
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen…"
            className="w-full rounded-md border border-app-border bg-app-surface-muted px-2.5 py-1.5 text-xs outline-none focus:border-blue-400"
          />
        </div>
      )}

      {!collapsed && error && (
        <div className="mx-3 mt-2 px-2 py-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded shrink-0">
          {error}
        </div>
      )}

      <div className={`flex-1 overflow-y-auto min-h-0 ${collapsed ? 'px-0' : 'px-2.5 py-3'}`}>
        {!collapsed && loading && <InlineLoadingState label="Lade…" className="justify-start px-1 py-3" />}
        {!collapsed && !loading && filtered.length === 0 && (
          <p className="px-1 py-3 text-sm text-app-text-subtle">
            {search ? 'Keine Treffer.' : 'Keine Ausschreibungen.'}
          </p>
        )}

        {!collapsed && !loading && inProgress.length > 0 && (
          <ListSection title="In Bearbeitung" count={inProgress.length}>
            {inProgress.map((it) => (
              <SidebarRow key={it.id} item={it} selected={selectedId === it.id} sectionDot="blue" />
            ))}
          </ListSection>
        )}

        {!collapsed && !loading && drafts.length > 0 && (
          <ListSection title="Entwürfe" count={drafts.length}>
            {drafts.map((it) => (
              <SidebarRow key={it.id} item={it} selected={selectedId === it.id} sectionDot="gray" />
            ))}
          </ListSection>
        )}

        {!collapsed && !loading && ready.length > 0 && (
          <ListSection title="Versandbereit" count={ready.length}>
            {ready.map((it) => (
              <SidebarRow key={it.id} item={it} selected={selectedId === it.id} sectionDot="orange" />
            ))}
          </ListSection>
        )}

        {!collapsed && !loading && moreCount > 0 && (
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setMoreOpen((v) => !v)}
              className="w-full px-1 py-1.5 rounded-md text-left text-xs font-medium text-app-text-muted hover:bg-app-surface/50 flex items-center justify-between"
            >
              <span>
                Weitere ({moreCount})
              </span>
              <span className="font-normal">{moreOpen ? '−' : '+'}</span>
            </button>
            {moreOpen && (
              <div className="mt-1 space-y-3">
                {sent.length > 0 && (
                  <ListSection title="Versendet" count={sent.length} nested>
                    {sent.map((it) => (
                      <SidebarRow
                        key={it.id}
                        item={it}
                        selected={selectedId === it.id}
                        sectionDot="violet"
                      />
                    ))}
                  </ListSection>
                )}
                {finished.length > 0 && (
                  <ListSection title="Abgeschlossen" count={finished.length} nested>
                    {finished.map((it) => (
                      <SidebarRow
                        key={it.id}
                        item={it}
                        selected={selectedId === it.id}
                        sectionDot="muted"
                      />
                    ))}
                  </ListSection>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {!collapsed && !hideFooterLink && (
        <div className="border-t border-app-border/80 bg-app-surface px-3 py-2.5 shrink-0">
          <Link
            to={ausschreibungenListPath()}
            className="inline-flex items-center gap-1.5 text-[11px] font-medium text-pipedrive-blue hover:underline"
          >
            <List className="w-3.5 h-3.5 opacity-80" />
            Alle Ausschreibungen (Listen-Ansicht)
          </Link>
        </div>
      )}
    </aside>
  );
};

const ListSection: React.FC<{
  title: string;
  count: number;
  nested?: boolean;
  children: React.ReactNode;
}> = ({ title, count, nested, children }) => (
  <div className={nested ? '' : 'mb-4'}>
    <div className="px-1 mb-2">
      <h3 className="text-xs font-medium text-app-text-muted truncate">
        {title} ({count})
      </h3>
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const SidebarRow: React.FC<{
  item: AusschreibungListItem;
  selected: boolean;
  sectionDot: SectionDot;
}> = ({ item, selected, sectionDot }) => {
  const headline =
    item.customer_name?.trim() ||
    item.customer_company?.trim() ||
    item.title?.trim() ||
    'Ohne Titel';
  const secondary =
    item.title?.trim() && item.title.trim() !== headline
      ? item.title.trim()
      : item.customer_company?.trim() && item.customer_company.trim() !== headline
      ? item.customer_company.trim()
      : item.customer_city?.trim() ?? '';

  const days = daysUntil(item.deadline);
  const urgent = days != null && days <= 7;
  let fristLine: React.ReactNode = null;
  if (days != null) {
    if (days < 0) {
      fristLine = <span className="text-red-600 font-medium">Frist überfällig</span>;
    } else if (days === 0) {
      fristLine = <span className={urgent ? 'text-orange-600 font-medium' : 'text-app-text-subtle'}>Frist heute</span>;
    } else if (days === 1) {
      fristLine = <span className="text-orange-600 font-medium">Frist in 1T</span>;
    } else if (days <= 7) {
      fristLine = <span className="text-orange-600 font-medium">Frist in {days}T</span>;
    } else if (item.deadline) {
      fristLine = <span className="text-app-text-subtle">{fmtDate(item.deadline)}</span>;
    }
  } else if (item.deadline) {
    fristLine = <span className="text-app-text-subtle">{fmtDate(item.deadline)}</span>;
  }

  const gross = formatTenderGrossEuro(item.offer_draft);
  const amountDisplay = gross ?? '--';

  return (
    <Link
      to={ausschreibungDetailPath({ id: item.id, display_id: item.display_id })}
      className={`block rounded-lg border px-2.5 py-2.5 transition ${
        selected
          ? 'bg-app-surface border-app-border/90 shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
          : 'border-app-border/50 bg-app-surface/70 hover:border-app-border hover:bg-app-surface'
      }`}
    >
      <div className="flex gap-2.5">
        <span
          className={`mt-1 w-2 h-2 rounded-full shrink-0 ${dotClass[sectionDot]}`}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <span className="text-sm font-semibold text-app-text leading-tight truncate">
              {headline}
            </span>
            <span className="text-xs text-app-text-subtle tabular-nums shrink-0 pt-0.5">
              {shortDisplayId(item.display_id, item.id)}
            </span>
          </div>
          {secondary ? (
            <p className="text-xs text-app-text-subtle mt-1 leading-snug line-clamp-2">{secondary}</p>
          ) : null}
          <div className="flex items-end justify-between gap-2 mt-2">
            <div className="text-[11px] min-w-0">{fristLine}</div>
            <div className="text-sm font-semibold text-app-text tabular-nums shrink-0">
              {amountDisplay}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};
